import * as types from './constants';
import { GetRequest, api } from '../../../Api';
import axios from '../../../Api/axiosConfig';

const OVERVIEW_URI = 'admin/dashboard/overview';
const USERS_URI = 'admin/dashboard/users';
const EVENTS_URI = 'admin/dashboard/events';
const NOMINEE_VOTES_URI = (id) => `admin/dashboard/nominees/${id}/votes`;
const PROMO_CODES_URI = 'promo-codes';

// Success matches the backend's Controller::getResponse contract — any 2xx
// status is a real payload; anything else surfaces to the user as an error.
const isSuccess = (res) =>
    res && typeof res === 'object' && res.status >= 200 && res.status < 300 && 'data' in res;

const errorMessage = (res, fallback) => (res && res.message) || fallback;

const makeThunk = (uri, loadingType, loadType, errorType, fallbackError) => (
    token,
    params = {}
) => (dispatch) => {
    dispatch({ type: loadingType, payload: true });
    GetRequest(params, token, uri)
        .then((res) => {
            if (isSuccess(res)) {
                dispatch({ type: loadType, payload: res.data });
            } else {
                dispatch({ type: errorType, payload: errorMessage(res, fallbackError) });
            }
        })
        .catch(() => dispatch({ type: errorType, payload: fallbackError }))
        .finally(() => dispatch({ type: loadingType, payload: false }));
};

export const GetOverview = makeThunk(
    OVERVIEW_URI,
    types.SET_OVERVIEW_LOADING,
    types.LOAD_OVERVIEW,
    types.SET_OVERVIEW_ERROR,
    'Failed to load overview'
);

export const GetUsers = makeThunk(
    USERS_URI,
    types.SET_USERS_LOADING,
    types.LOAD_USERS,
    types.SET_USERS_ERROR,
    'Failed to load users'
);

export const GetEvents = makeThunk(
    EVENTS_URI,
    types.SET_EVENTS_LOADING,
    types.LOAD_EVENTS,
    types.SET_EVENTS_ERROR,
    'Failed to load events'
);

export const ClearOverviewError = () => (dispatch) =>
    dispatch({ type: types.SET_OVERVIEW_ERROR, payload: null });

export const ClearUsersError = () => (dispatch) =>
    dispatch({ type: types.SET_USERS_ERROR, payload: null });

export const ClearEventsError = () => (dispatch) =>
    dispatch({ type: types.SET_EVENTS_ERROR, payload: null });

// Drill-down thunk: votes cast for a single nominee. Keyed by nominee id so the
// UI can preload one nominee's page without clobbering another still on screen.
export const GetNomineeVotes = (token, nomineeId, params = {}) => (dispatch) => {
    dispatch({ type: types.SET_NOMINEE_VOTES_LOADING, payload: { nomineeId, loading: true } });
    GetRequest(params, token, NOMINEE_VOTES_URI(nomineeId))
        .then((res) => {
            if (isSuccess(res)) {
                dispatch({
                    type: types.LOAD_NOMINEE_VOTES,
                    payload: { nomineeId, data: res.data }
                });
            } else {
                dispatch({
                    type: types.SET_NOMINEE_VOTES_ERROR,
                    payload: {
                        nomineeId,
                        error: errorMessage(res, 'Failed to load votes for this nominee')
                    }
                });
            }
        })
        .catch(() =>
            dispatch({
                type: types.SET_NOMINEE_VOTES_ERROR,
                payload: { nomineeId, error: 'Failed to load votes for this nominee' }
            })
        )
        .finally(() =>
            dispatch({
                type: types.SET_NOMINEE_VOTES_LOADING,
                payload: { nomineeId, loading: false }
            })
        );
};

export const ClearNomineeVotes = (nomineeId) => (dispatch) =>
    dispatch({ type: types.CLEAR_NOMINEE_VOTES, payload: { nomineeId } });

// Admin promo-code creation. Not routed through PostRequest because that helper
// serializes an HTMLFormElement; here the caller passes a plain values object
// and we build a FormData directly, keeping the Laravel side unchanged.
export const CreatePromoCode = (token, attrs) => async (dispatch) => {
    dispatch({ type: types.SET_PROMO_CREATE_LOADING, payload: true });
    dispatch({ type: types.SET_PROMO_CREATE_ERROR, payload: null });

    const formData = new FormData();
    formData.append('event_id', String(attrs.event_id));
    if (attrs.max_redemptions) formData.append('max_redemptions', String(attrs.max_redemptions));
    if (attrs.expires_at) formData.append('expires_at', String(attrs.expires_at));

    try {
        const response = await axios.post(api + PROMO_CODES_URI, formData, {
            headers: {
                Accept: 'application/json',
                Authorization: 'Bearer ' + token
            }
        });
        const res = response.data;
        if (isSuccess(res)) {
            dispatch({ type: types.PROMO_CREATED, payload: res.data });
            return res.data;
        }
        dispatch({
            type: types.SET_PROMO_CREATE_ERROR,
            payload: errorMessage(res, 'Could not generate the promo code.')
        });
        return null;
    } catch (err) {
        const serverMessage = err?.response?.data?.message;
        dispatch({
            type: types.SET_PROMO_CREATE_ERROR,
            payload: serverMessage || 'Could not generate the promo code.'
        });
        return null;
    } finally {
        dispatch({ type: types.SET_PROMO_CREATE_LOADING, payload: false });
    }
};

export const ClearPromoCreated = () => (dispatch) =>
    dispatch({ type: types.CLEAR_PROMO_CREATED });
